import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import {
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  MimeRejection,
  SkpEnvironment,
  TransferDraft,
} from "../../../entities";
import { useTransferDraft } from "../../../providers/transferDraft";
import { getHumanReadableFileSize } from "../../../utils/units";
import { useSkpEnvironment } from "../../../providers/environment";
import { useWindowDimensions } from "../../../hooks/useWindowDimensions";
import { Spacer } from "../../../components/layout";
import { TransitionProps } from "@mui/material/transitions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { UploadStatus, useUploadStatus } from "../../../providers/uploadStatus";
import { Spinner } from "../../../components/spinner";
import { TransferAbortionState } from "./send";
import { alpha } from "@mui/material/styles";

/** Desktop mime-rejection layout width (px). */
const MIME_REJECTION_DESKTOP_DIALOG_WIDTH_PX = 641;
const UPLOAD_RING_DEFAULT_PX = 184;
const UPLOAD_RING_MIME_DESKTOP_PX = 110;

/**
 * One shared timeline for layout, paper width, ring size, and flex morphs.
 * Keep Tailwind `sm:duration-[…ms]` below in sync with this value.
 */
const UPLOAD_DIALOG_LAYOUT_MS = 1000;
/** Fluid layout curve (Material-style); avoids overly long ease-out tails that read as janky. */
const UPLOAD_DIALOG_MOTION_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const ringSizeTransitionStyle = `width ${UPLOAD_DIALOG_LAYOUT_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}, height ${UPLOAD_DIALOG_LAYOUT_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}, min-width ${UPLOAD_DIALOG_LAYOUT_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}, min-height ${UPLOAD_DIALOG_LAYOUT_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}`;

/**
 * When upload is interrupted, the wave loop stops and WAAPI settles the halo to
 * scale(1)/opacity(1). Shorter and snappier than UPLOAD_DIALOG_MOTION_EASE so it
 * feels decisive vs the slow layout ring transition.
 */
const UPLOAD_PULSE_INTERRUPT_SETTLE_MS = 520;
const UPLOAD_PULSE_INTERRUPT_SETTLE_EASE = "cubic-bezier(0.45, 0, 0.25, 1)";

/** Mime rejection body: same duration as layout morph; opacity lags slightly for a clean stagger. */
const MIME_REVEAL_MS = UPLOAD_DIALOG_LAYOUT_MS;
const MIME_REVEAL_OPACITY_DELAY_IN_MS = 160;

export type MimeRejectionInfo = Pick<MimeRejection, "fileName"> & {
  isSkipping: boolean;
  onSkipFile: () => void;
  onReturnToDraft: () => void;
};

type UploadDialogProp = {
  error: Error | null;
  onDismiss: () => void;
  draft: TransferDraft;
  progress: number;
  onConfirmAbort: () => Promise<void> | void;
  abortState: TransferAbortionState;
  mimeRejection: MimeRejectionInfo | null;
};

type UploadedChunkInfoProp = {
  progress: number;
  isConfirmingAbort: boolean;
  error: Error | null;
};

type CircularProgressIndicatorProp = {
  progress: number;
  isInterrupted: boolean;
  ringDiameterPx?: number;
};

type GradientCircleProp = {
  outerPx: number;
  innerPx: number;
};

type UploadDialogDismissButtonProp = {
  onDismiss: () => void;
};

type ProgressDetailsProp = {
  progress: number;
  isConfirmingAbort: boolean;
  error: Error | null;
  /** Desktop mime rejection: header only; body is rendered full-width below the ring row. */
  mimeRejection?: MimeRejectionInfo | null;
};

type TitleProp = {
  isConfirmingAbort: boolean;
};

const dialogFadeTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Fade ref={ref} {...props} />;
});

// TODO implement ETA & speed for the next iteration.
/**
 * This component represents a dialog which is rendered during an on-going transfer
 * upload.
 *
 * @returns JSX element representing a dialog.
 *
 * @see{@link Dialog}.
 */
export default function UploadDialog({
  onDismiss,
  progress,
  onConfirmAbort,
  abortState,
  error,
  mimeRejection,
}: UploadDialogProp) {
  const { t } = useTranslation();
  const uploadStatus: UploadStatus = useUploadStatus();
  const { isMobileWidth } = useWindowDimensions();
  const isMimeDesktop = Boolean(mimeRejection) && !isMobileWidth;
  const mimeRejectionFileKey = mimeRejection?.fileName ?? "";

  const [mimeDesktopBodyRevealed, setMimeDesktopBodyRevealed] = useState(false);

  useEffect(() => {
    if (!isMimeDesktop) {
      setMimeDesktopBodyRevealed(false);
      return;
    }
    setMimeDesktopBodyRevealed(false);
    const timer = window.setTimeout(() => {
      setMimeDesktopBodyRevealed(true);
    }, UPLOAD_DIALOG_LAYOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isMimeDesktop, mimeRejectionFileKey]);

  // the dialog is in a state where the user is asked to confirm their intent to abort the transfer upload.
  const [isShowingAbortConfirmation, setIsShowingAbortConfirmation] =
    useState<boolean>(false);
  // the abort request is being initiated after the user has confirmed their intent to abort the transfer upload.
  // but we are not yet in the process of aborting the transfer upload.
  const [isInitiatingAbortAfterConfirm, setIsInitiatingAbortAfterConfirm] =
    useState<boolean>(false);

  useEffect(() => {
    if (progress >= 100 && isShowingAbortConfirmation) {
      setIsShowingAbortConfirmation(false);
    }
  }, [progress, isShowingAbortConfirmation]);

  useEffect(() => {
    if (!abortState || abortState === TransferAbortionState.none) {
      setIsInitiatingAbortAfterConfirm(false);
    }
  }, [abortState]);

  const handleConfirmAbort = async () => {
    if (
      abortState === TransferAbortionState.aborting ||
      isInitiatingAbortAfterConfirm
    ) {
      return;
    }
    setIsInitiatingAbortAfterConfirm(true);
    await onConfirmAbort();
  };

  const uploadRingColumn = (
    <div
      className={`flex w-full flex-col items-center sm:transition-[margin-left,margin-right] sm:duration-[1000ms] sm:ease-[cubic-bezier(0.4,0,0.2,1)] ${
        isMimeDesktop
          ? "sm:ml-0 sm:mr-[50px] sm:w-auto sm:shrink-0 sm:items-center"
          : "sm:ml-36 sm:mr-3 sm:w-auto sm:shrink-0 sm:items-center"
      }`}
    >
      <div className="flex w-full flex-row items-center justify-center sm:gap-0">
        <div
          className={`relative flex w-full shrink-0 items-center justify-center sm:w-auto ${
            isMimeDesktop ? "sm:h-[110px] sm:min-h-[110px]" : ""
          }`}
        >
          {uploadStatus === UploadStatus.aborted && isMobileWidth && (
            <img
              src={process.env.PUBLIC_URL + "/assets/disconnected-cables.svg"}
              alt=""
              className="
                pointer-events-none
                absolute left-1/2 top-1/2 z-10
                h-[100vw] min-w-full
                -translate-x-1/2 -translate-y-1/2 rotate-90
                animate-fade-in
                object-contain"
            />
          )}
          <CircularProgressIndicator
            progress={progress}
            isInterrupted={
              !!mimeRejection ||
              isInitiatingAbortAfterConfirm ||
              abortState === TransferAbortionState.aborting
            }
            ringDiameterPx={
              isMimeDesktop
                ? UPLOAD_RING_MIME_DESKTOP_PX
                : UPLOAD_RING_DEFAULT_PX
            }
          />
        </div>
      </div>
    </div>
  );

  const uploadStatusColumn = (
    <div className="flex w-full min-w-0 flex-col items-center overflow-hidden sm:w-auto sm:max-w-[min(2000px,calc(100vw-8rem))] sm:flex-1 sm:shrink-0 sm:basis-0">
      <div
        className={`h-8 shrink-0 sm:transition-[width,margin] sm:duration-[1000ms] sm:ease-[cubic-bezier(0.4,0,0.2,1)] ${
          isMimeDesktop
            ? "sm:!m-0 sm:!h-0 sm:!w-0 sm:!min-w-0 sm:overflow-hidden"
            : "sm:h-0 sm:w-12"
        }`}
      />
      {mimeRejection && isMobileWidth ? (
        <MimeTypeRejectionOverlay mimeRejection={mimeRejection} />
      ) : (
        <div className="flex w-full min-w-0 flex-col items-center justify-center">
          <div className="flex w-full min-w-0 flex-col items-start justify-center pt-10 sm:flex-row sm:items-center sm:pt-0">
            <ProgressDetails
              error={error}
              progress={progress}
              isConfirmingAbort={isShowingAbortConfirmation}
              mimeRejection={
                mimeRejection && !isMobileWidth ? mimeRejection : null
              }
            />
            <div className="h-10 w-5 sm:h-0" />
            {uploadStatus === UploadStatus.ongoing &&
              !isShowingAbortConfirmation &&
              !mimeRejection && (
                <div className="pt-2">
                  <UploadAbortButton
                    onClick={() => setIsShowingAbortConfirmation(true)}
                    disabled={abortState === TransferAbortionState.aborting}
                  />
                </div>
              )}
          </div>
          {isShowingAbortConfirmation &&
            !mimeRejection &&
            uploadStatus !== UploadStatus.aborted && (
              <>
                <Spacer />
                <div
                  className={`z-30 flex gap-3 sm:absolute sm:bottom-10 sm:right-32 ${
                    abortState === TransferAbortionState.aborting ||
                    isInitiatingAbortAfterConfirm
                      ? "cursor-wait"
                      : ""
                  }`}
                >
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => {
                        if (
                          abortState !== TransferAbortionState.aborting &&
                          !isInitiatingAbortAfterConfirm
                        ) {
                          setIsShowingAbortConfirmation(false);
                        }
                      }}
                      disabled={
                        abortState === TransferAbortionState.aborting ||
                        isInitiatingAbortAfterConfirm
                      }
                    >
                      {t(`No`)}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={handleConfirmAbort}
                      disabled={
                        abortState === TransferAbortionState.aborting ||
                        isInitiatingAbortAfterConfirm
                      }
                    >
                      {isInitiatingAbortAfterConfirm ? (
                        <Spinner color="white" size={16} compact={true} />
                      ) : (
                        t(`Yes`)
                      )}
                    </Button>
                  </>
                </div>
              </>
            )}
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      open={true}
      maxWidth={isMimeDesktop ? false : "md"}
      fullScreen={isMobileWidth}
      fullWidth={!isMimeDesktop}
      onClose={(event, reason) => {
        console.log("onClose", reason);
        const isBackdropOrEsc =
          reason === "backdropClick" || reason === "escapeKeyDown";

        if (mimeRejection && isBackdropOrEsc) {
          return;
        }

        // we block dialog dismissal via backdrop click or ESC key,
        // while transfer upload is going on and user has not confirmed abort,
        // -- changed to use isInitiatingAbortAfterConfirm || isAbortInProgress
        if (
          progress < 100 &&
          !(
            isInitiatingAbortAfterConfirm ||
            abortState === TransferAbortionState.aborting
          ) &&
          isBackdropOrEsc
        ) {
          console.log("onClose blocked:");
          console.log("progress", progress);
          console.log("abortState", abortState);
          console.log(
            "isInitiatingAbortAfterConfirm",
            isInitiatingAbortAfterConfirm
          );
          return;
        }

        onDismiss();
      }}
      PaperProps={{
        sx: (theme) => ({
          borderRadius: "14px",
          boxShadow: isMobileWidth
            ? "none"
            : "0px 24px 24px rgba(0, 0, 0, 0.16)",
          ...(isMimeDesktop && {
            width: MIME_REJECTION_DESKTOP_DIALOG_WIDTH_PX,
            maxWidth: MIME_REJECTION_DESKTOP_DIALOG_WIDTH_PX,
            maxHeight: "min(92vh, 720px)",
            overflowY: "auto",
          }),
          ...(!isMobileWidth && {
            transition: theme.transitions.create(["width", "max-width"], {
              duration: UPLOAD_DIALOG_LAYOUT_MS,
              easing: UPLOAD_DIALOG_MOTION_EASE,
            }),
          }),
        }),
      }}
      TransitionComponent={isMobileWidth ? dialogFadeTransition : undefined}
      transitionDuration={isMobileWidth ? 720 : UPLOAD_DIALOG_LAYOUT_MS}
    >
      <div
        className={`relative flex h-screen select-none flex-col place-items-center p-10 sm:transition-[padding] sm:duration-[1000ms] sm:ease-[cubic-bezier(0.4,0,0.2,1)] ${
          isMimeDesktop
            ? "sm:h-auto sm:min-h-0 sm:flex-row sm:place-items-stretch sm:items-start sm:overflow-visible sm:px-[80px] sm:py-[80px]"
            : "overflow-y-clip sm:h-[368px] sm:flex-row sm:items-center sm:px-10 sm:py-20"
        }`}
      >
        {uploadStatus === UploadStatus.aborted && !isMobileWidth && (
          <img
            src={process.env.PUBLIC_URL + "/assets/disconnected-cables.svg"}
            alt=""
            className="
            pointer-events-none 
            absolute left-[155px] top-0 z-10 
            h-full max-w-[360px]
            animate-fade-in
            object-contain"
          />
        )}
        {(uploadStatus === UploadStatus.success ||
          uploadStatus === UploadStatus.failed ||
          uploadStatus === UploadStatus.aborted) && (
          <div className="self-end sm:hidden">
            <UploadDialogDismissButton onDismiss={onDismiss} />
          </div>
        )}
        <div
          className={`mt-[15vh] flex w-full flex-col items-center sm:mt-0 ${
            isMimeDesktop
              ? "sm:flex-col sm:items-stretch sm:gap-0"
              : "sm:flex-row sm:items-center"
          }`}
        >
          {isMimeDesktop ? (
            <>
              <div className="flex w-full flex-row items-start">
                {uploadRingColumn}
                {uploadStatusColumn}
              </div>
              {mimeRejection ? (
                <MimeRejectionDesktopBody
                  mimeRejection={mimeRejection}
                  revealed={mimeDesktopBodyRevealed}
                />
              ) : null}
            </>
          ) : (
            <>
              {uploadRingColumn}
              {uploadStatusColumn}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function MimeRejectionTitleBlock({ fileName }: { fileName: string }) {
  const { t } = useTranslation();

  return (
    <div className="z-30 flex min-w-0 flex-col text-left">
      <div
        className="text-[26px] font-medium sm:font-normal"
        style={{
          textShadow: "0 0 4px #fff, 0 0 8px #fff, 0 0 14px #fff",
        }}
      >
        {t(`Transfer paused`)}
      </div>
      <div className="mt-1 min-w-0 max-w-full break-words text-sm font-medium leading-snug text-[#C40606]">
        {fileName} - {t(`File type not allowed`)}
      </div>
    </div>
  );
}

function MimeRejectionDetailsAndButtons({
  mimeRejection,
  leadSpacingClass = "mt-4",
}: {
  mimeRejection: MimeRejectionInfo;
  leadSpacingClass?: string;
}) {
  const { t } = useTranslation();
  const { fileName, isSkipping, onSkipFile, onReturnToDraft } = mimeRejection;
  const ext = fileName.includes(".")
    ? fileName.slice(fileName.lastIndexOf("."))
    : "";

  return (
    <>
      <div className={`${leadSpacingClass} text-sm text-[#444]`}>
        <Trans
          i18nKey="Uploading <bold>{{ext}}</bold> files is not allowed in this mailbox. Please choose an option:"
          values={{ ext }}
          components={{ bold: <span className="font-bold" /> }}
        />
      </div>
      <ul className="mt-2 list-disc pl-5 text-sm text-[#444]">
        <li>
          <Trans
            i18nKey="<bold>Skip file</bold> and continue the transfer without this file"
            components={{ bold: <span className="font-bold" /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="Abort the transfer and <bold>return to the draft</bold>"
            components={{ bold: <span className="font-bold" /> }}
          />
        </li>
      </ul>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={onSkipFile}
          disabled={isSkipping}
          sx={{ minWidth: 120 }}
        >
          {isSkipping ? (
            <Spinner color="white" size={16} compact={true} />
          ) : (
            t(`Skip file`)
          )}
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={onReturnToDraft}
          disabled={isSkipping}
        >
          {t(`Return to draft`)}
        </Button>
      </div>
    </>
  );
}

function MimeRejectionDesktopBody({
  mimeRejection,
  revealed,
}: {
  mimeRejection: MimeRejectionInfo;
  revealed: boolean;
}) {
  return (
    <div
      className={`z-30 w-full min-w-0 max-w-full self-stretch overflow-hidden sm:self-auto ${
        revealed
          ? "mt-4 max-h-[min(2000px,120vh)] translate-y-0 opacity-100"
          : "pointer-events-none mt-0 max-h-0 translate-y-3 opacity-0"
      }`}
      style={{
        transition: `max-height ${MIME_REVEAL_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}, opacity ${MIME_REVEAL_MS}ms ${UPLOAD_DIALOG_MOTION_EASE} ${
          revealed ? `${MIME_REVEAL_OPACITY_DELAY_IN_MS}ms` : "0ms"
        }, transform ${MIME_REVEAL_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}, margin-top ${MIME_REVEAL_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}`,
      }}
      aria-hidden={!revealed}
    >
      <MimeRejectionDetailsAndButtons
        mimeRejection={mimeRejection}
        leadSpacingClass="mt-0"
      />
    </div>
  );
}

function MimeTypeRejectionOverlay({
  mimeRejection,
}: {
  mimeRejection: MimeRejectionInfo;
}) {
  return (
    <div className="z-30 flex animate-fade-in flex-col justify-center">
      <MimeRejectionTitleBlock fileName={mimeRejection.fileName} />
      <MimeRejectionDetailsAndButtons mimeRejection={mimeRejection} />
    </div>
  );
}

/**
 * True when getComputedStyle transform is ~identity (wave sampled at 0% keyframe).
 * Mime rejection shrinks the ring in the same commit as interrupt; that often
 * restarts CSS animations at t=0, so sampling would otherwise match rest and WAAPI shows nothing.
 */
function isIdentity2DTransformCss(transform: string): boolean {
  if (!transform || transform === "none") return true;
  const match = /^matrix\(([^)]+)\)\s*$/.exec(transform.trim());
  if (!match) return false;
  const parts = match[1]
    .split(/\s*,\s*/)
    .map((v) => Number.parseFloat(v.trim()));
  if (parts.length < 6) return false;
  const [a, b, c, d, e, f] = parts;
  return (
    Math.abs(a - 1) < 0.02 &&
    Math.abs(b) < 0.02 &&
    Math.abs(c) < 0.02 &&
    Math.abs(d - 1) < 0.02 &&
    Math.abs(e) < 1 &&
    Math.abs(f) < 1
  );
}

const INTERRUPT_SETTLE_REST: Keyframe = {
  transform: "scale(1)",
  opacity: "1",
};

/**
 * This component represents a determinate circular progress indicator for
 * the on-going transfer upload with progress value inside of it.
 *
 * @returns JSX element representing a circular progress indicator.
 *
 * @see{@link CircularProgress}.
 */
function CircularProgressIndicator({
  progress,
  isInterrupted,
  ringDiameterPx = UPLOAD_RING_DEFAULT_PX,
}: CircularProgressIndicatorProp) {
  const uploadStatus: UploadStatus = useUploadStatus();
  const [isUploadComplete, setIsUploadComplete] = useState<boolean>(false);
  const pulseRef = useRef<HTMLDivElement>(null);
  /** Opacity pulse on the progress stack (`animate-upload-progress-bar`); must settle with the wave on interrupt. */
  const progressRingPulseRef = useRef<HTMLDivElement>(null);
  const settleAnimationsRef = useRef<[Animation | null, Animation | null]>([
    null,
    null,
  ]);

  const d = ringDiameterPx;
  const pulseBox = Math.round(d * (180 / UPLOAD_RING_DEFAULT_PX));
  const gradientOuterPx = Math.round(d * (180 / UPLOAD_RING_DEFAULT_PX));
  const gradientInnerPx = Math.round(d * (160 / UPLOAD_RING_DEFAULT_PX));
  const progressLabelFontPx = Math.max(
    18,
    Math.round(40 * (d / UPLOAD_RING_DEFAULT_PX))
  );

  useEffect(() => {
    if (
      (uploadStatus === UploadStatus.failed ||
        uploadStatus === UploadStatus.aborted ||
        uploadStatus === UploadStatus.success) &&
      !isUploadComplete
    ) {
      setIsUploadComplete(true);
    }
  }, [progress, isUploadComplete, uploadStatus]);

  const cancelPulseSettles = () => {
    settleAnimationsRef.current[0]?.cancel();
    settleAnimationsRef.current[1]?.cancel();
    settleAnimationsRef.current = [null, null];
  };

  const freezePulseAndSettle = (el: HTMLElement): Animation => {
    const computed = getComputedStyle(el);
    const transform = computed.transform;
    const opacity = computed.opacity;
    const opacityN = Number.parseFloat(opacity);

    const stuckAtRestKeyframe =
      isIdentity2DTransformCss(transform) && opacityN >= 0.9;

    const from: Keyframe = stuckAtRestKeyframe
      ? { transform: "scale(1.42)", opacity: "0.32" }
      : { transform, opacity };

    el.style.animation = "none";
    el.style.transform = String(from.transform ?? "");
    el.style.opacity = String(from.opacity ?? "");
    void el.offsetWidth;

    return el.animate([from, INTERRUPT_SETTLE_REST], {
      duration: UPLOAD_PULSE_INTERRUPT_SETTLE_MS,
      easing: UPLOAD_PULSE_INTERRUPT_SETTLE_EASE,
      fill: "forwards",
    });
  };

  const clearPulseInlineAnimationOverrides = (el: HTMLElement | null) => {
    if (!el) return;
    el.style.animation = "";
    el.style.transform = "";
    el.style.opacity = "";
    void el.offsetWidth;
  };

  const isInterruptedRef = useRef(isInterrupted);
  isInterruptedRef.current = isInterrupted;
  const isUploadCompleteRef = useRef(isUploadComplete);
  isUploadCompleteRef.current = isUploadComplete;

  // When not interrupted: clear WAAPI / inline overrides before paint (avoids flash on resume).
  useLayoutEffect(() => {
    if (isUploadComplete) {
      cancelPulseSettles();
      return;
    }
    if (!isInterrupted) {
      cancelPulseSettles();
      clearPulseInlineAnimationOverrides(pulseRef.current);
      clearPulseInlineAnimationOverrides(progressRingPulseRef.current);
    }
  }, [isInterrupted, isUploadComplete]);

  // Defer freeze+settle by two frames so width/height transitions on the ring can start.
  // Synchronous getComputedStyle + forced layout in the same frame as d/pulseBox change
  // skips the size transition (browser jumps to the end state).
  useEffect(() => {
    if (isUploadComplete || !isInterrupted) {
      return;
    }

    cancelPulseSettles();

    let raf1 = 0;
    let raf2 = 0;

    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        if (!isInterruptedRef.current || isUploadCompleteRef.current) {
          return;
        }
        const waveEl = pulseRef.current;
        const ringPulseEl = progressRingPulseRef.current;
        settleAnimationsRef.current = [
          waveEl ? freezePulseAndSettle(waveEl) : null,
          ringPulseEl ? freezePulseAndSettle(ringPulseEl) : null,
        ];
      });
    });

    return () => {
      if (raf1 !== 0) {
        window.cancelAnimationFrame(raf1);
      }
      if (raf2 !== 0) {
        window.cancelAnimationFrame(raf2);
      }
    };
  }, [isInterrupted, isUploadComplete]);

  const clampedProgress = Math.min(Math.round(progress), 100);

  const uploadProgressText =
    uploadStatus === UploadStatus.failed ? "" : `${clampedProgress}%`;

  const shouldShowProgressText =
    uploadStatus === UploadStatus.ongoing ||
    (uploadStatus === UploadStatus.success && clampedProgress >= 100);

  return (
    <div
      className="pointer-events-none relative isolate flex items-center justify-center"
      style={{
        strokeDashoffset: 0,
        width: d,
        height: d,
        minWidth: d,
        minHeight: d,
        transition: ringSizeTransitionStyle,
      }}
    >
      {/* Flex-center wrapper so ping keyframes (transform: scale) do not clobber translate centering */}
      <div className="absolute inset-0 z-0 flex items-center justify-center">
        <div
          ref={pulseRef}
          className={`rounded-full bg-[#DEDEDE] ${
            isUploadComplete
              ? "animate-ping-upload-progress-bar"
              : "animate-ping-wave-upload-progress-bar"
          }`}
          style={{
            width: pulseBox,
            height: pulseBox,
            transition: ringSizeTransitionStyle,
          }}
        />
      </div>
      <GradientCircle outerPx={gradientOuterPx} innerPx={gradientInnerPx} />
      <div
        ref={progressRingPulseRef}
        className="relative z-[2] flex animate-upload-progress-bar items-center justify-center"
        style={{
          width: d,
          height: d,
          minWidth: d,
          minHeight: d,
          transition: ringSizeTransitionStyle,
        }}
      >
        <CircularProgress
          variant="determinate"
          value={
            uploadStatus === UploadStatus.failed ||
            uploadStatus === UploadStatus.aborted
              ? undefined
              : progress
          }
          size={d}
          sx={{
            position: "relative",
            top: 0,
            left: 0,
            flexShrink: 0,
            display: "block",
            width: d,
            height: d,
            minWidth: d,
            minHeight: d,
            transition: ringSizeTransitionStyle,
            "& svg": {
              width: "100%",
              height: "100%",
              display: "block",
              transition: ringSizeTransitionStyle,
            },
          }}
        />
      </div>
      {shouldShowProgressText && (
        <div
          className="absolute left-1/2 top-1/2 z-[3] -translate-x-1/2 -translate-y-1/2 font-medium text-[#FEFFFE]"
          style={{
            fontSize: progressLabelFontPx,
            transition: `font-size ${UPLOAD_DIALOG_LAYOUT_MS}ms ${UPLOAD_DIALOG_MOTION_EASE}`,
          }}
        >
          {uploadProgressText}
        </div>
      )}
    </div>
  );
}

/**
 * This component represents a Gradient circle.
 *
 * @returns JSX element representing a circle with linear gradient.
 */
function GradientCircle({ outerPx, innerPx }: GradientCircleProp) {
  const uploadStatus: UploadStatus = useUploadStatus();
  const outerContainerStyle = {
    opacity:
      uploadStatus === UploadStatus.success ||
      uploadStatus === UploadStatus.failed ||
      uploadStatus === UploadStatus.aborted
        ? 0
        : 1,
    backgroundImage: "linear-gradient(to bottom, #F7F7F7, #E6E6E6, #999999)",
    transition: "opacity 1.5s cubic-bezier(0.4, 0, 0.6, 1)",
  };

  let innerContainerLinearGradient: string =
    "var(--upload-dialog-success-linear-gradient)";

  if (
    uploadStatus === UploadStatus.failed ||
    uploadStatus === UploadStatus.aborted
  ) {
    innerContainerLinearGradient =
      "var(--upload-dialog-failed-linear-gradient)";
  }

  const innerContainerStyle = {
    backgroundImage: innerContainerLinearGradient,
  };

  const outerCircleBgColor: string =
    uploadStatus === UploadStatus.failed ||
    uploadStatus === UploadStatus.aborted
      ? "var(--warning-light-color)"
      : "white";

  const dimensionTransition = ringSizeTransitionStyle;

  return (
    <div
      className="absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full drop-shadow-md"
      style={{
        backgroundColor: outerCircleBgColor,
        width: outerPx,
        height: outerPx,
        transition: dimensionTransition,
      }}
    >
      <div
        className="rounded-full"
        style={{
          ...innerContainerStyle,
          width: innerPx,
          height: innerPx,
          transition: dimensionTransition,
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          ...outerContainerStyle,
          width: innerPx,
          height: innerPx,
          transition: dimensionTransition,
        }}
      />
    </div>
  );
}

/**
 * This component displays details regarding transfer upload.
 *
 * @returns JSX element displaying a upload progress details.
 */
function ProgressDetails({
  progress,
  isConfirmingAbort,
  error,
  mimeRejection = null,
}: ProgressDetailsProp) {
  const uploadStatus: UploadStatus = useUploadStatus();
  const { t } = useTranslation();
  const environment: SkpEnvironment = useSkpEnvironment()!;
  const displaySignupLink: boolean =
    environment.self_register_enabled && !!environment.self_register_url;

  const { isMobileWidth } = useWindowDimensions();

  let uploadStatusText: string = "";

  let containerHeightMobile: string = "h-[18vh]";

  if (mimeRejection && !isMobileWidth) {
    return (
      <div className="z-30 flex w-full min-w-0 flex-col sm:h-auto sm:min-h-0">
        <div className="flex min-h-[110px] min-w-0 flex-col justify-center">
          <MimeRejectionTitleBlock fileName={mimeRejection.fileName} />
        </div>
      </div>
    );
  }

  switch (uploadStatus) {
    case UploadStatus.failed:
      uploadStatusText = t(`Failed`);
      break;
    case UploadStatus.success:
      uploadStatusText = t(`Completed`);
      break;
    case UploadStatus.aborted:
      uploadStatusText = t(`Cancelled`);
      break;
    case UploadStatus.ongoing:
      uploadStatusText = t(`Cancelled`);
      containerHeightMobile = "h-full";
      break;
    case UploadStatus.paused:
      uploadStatusText = t(`Paused`);
      containerHeightMobile = "h-full";
      break;
    case UploadStatus.idle:
      uploadStatusText = "";
      containerHeightMobile = "h-full";
      break;
    default:
      break;
  }

  return (
    <div
      className={`z-30 flex flex-col justify-center sm:h-[405px] ${containerHeightMobile}`}
    >
      <div className="h-0 sm:h-16" />
      {!isMobileWidth && <Spacer />}
      <div className="flex flex-row items-center justify-center gap-x-4 sm:justify-start">
        <Title
          key={`${uploadStatus}-${isConfirmingAbort ? "confirm" : "normal"}`}
          isConfirmingAbort={isConfirmingAbort}
        />
        {uploadStatus === UploadStatus.success && !isConfirmingAbort && (
          <CheckCircleIcon />
        )}
      </div>
      {uploadStatus === UploadStatus.ongoing && !isConfirmingAbort ? (
        <UploadedChunkInfo
          error={error}
          progress={progress}
          isConfirmingAbort={isConfirmingAbort}
        />
      ) : isConfirmingAbort ? (
        <UploadedChunkInfo
          error={error}
          progress={progress}
          isConfirmingAbort={isConfirmingAbort}
        />
      ) : (
        <div className="ml-[45px] text-[12px] font-medium text-[#747474] sm:ml-0">
          {uploadStatusText}
        </div>
      )}
      <Spacer />
      {uploadStatus === UploadStatus.success && displaySignupLink && (
        <div className="animate-fade-in">
          <SignupTextAndLink />
        </div>
      )}
      <div className="h-0 sm:h-11" />
    </div>
  );
}

const SignupTextAndLink = () => {
  const environment: SkpEnvironment = useSkpEnvironment()!;
  return (
    <div className="text-[12px] text-[#747474]">
      <Trans
        i18nKey="Would you also like to use TeamBeam <hyperlink> free of charge?</hyperlink>"
        components={{
          hyperlink: (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
              href={environment.self_register_url!.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--footer-link-color)",
                textDecoration: "none",
              }}
            />
          ),
        }}
      />
    </div>
  );
};

/**
 * This component represents title of the upload dialog.
 *
 * @returns JSX element displaying title upload dialog's title.
 */
function Title({ isConfirmingAbort }: TitleProp) {
  const uploadStatus: UploadStatus = useUploadStatus();
  const { t } = useTranslation();
  let uploadStatusTitle: string = "";

  if (isConfirmingAbort && uploadStatus !== UploadStatus.aborted) {
    uploadStatusTitle = t(`Cancel Transfer upload`);
  } else {
    switch (uploadStatus) {
      case UploadStatus.failed:
        uploadStatusTitle = t(`Transfer upload failed`);
        break;
      case UploadStatus.success:
        uploadStatusTitle = t(`Transfer sent`);
        break;
      case UploadStatus.ongoing:
        uploadStatusTitle = t(`Transfer is uploading`);
        break;
      case UploadStatus.paused:
        uploadStatusTitle = t(`Waiting for internet connection`);
        break;
      case UploadStatus.aborted:
        uploadStatusTitle = t(`Transfer upload cancelled`);
        break;
      default:
        break;
    }
  }

  return (
    <div
      className={`text-[26px] font-medium sm:font-normal`}
      style={{
        textShadow: "0 0 4px #fff, 0 0 8px #fff, 0 0 14px #fff",
      }}
    >
      {uploadStatusTitle}
    </div>
  );
}

/**
 * This component displays chunks that have been uploaded so far.
 *
 * @returns JSX element displaying uploaded chunks.
 */
function UploadedChunkInfo({
  progress,
  isConfirmingAbort,
}: UploadedChunkInfoProp) {
  const { t } = useTranslation();
  const transferDraft: TransferDraft = useTransferDraft();
  const uploadStatus: UploadStatus = useUploadStatus();

  if (isConfirmingAbort && uploadStatus !== UploadStatus.aborted) {
    return (
      <div className="animate-fade-in text-xs text-[#747474]">
        {t(`Do you really wish to abort transfer upload?`)}
      </div>
    );
  }

  if (uploadStatus === UploadStatus.aborted) {
    return (
      <div className="animate-fade-in text-xs text-[#747474]">
        {t(`Cancelled`)}
      </div>
    );
  }

  const totalFilesSize = transferDraft.files.reduce(
    (acc, [, file]) => acc + file.size,
    0
  );
  let uploadedBytes: number = (progress * totalFilesSize) / 100;
  return (
    <div className="animate-fade-in text-xs text-[#747474]">
      {getHumanReadableFileSize(uploadedBytes)} {t(`from`)}{" "}
      {getHumanReadableFileSize(totalFilesSize)}
    </div>
  );
}

const UploadDialogDismissButton = ({
  onDismiss,
}: UploadDialogDismissButtonProp) => {
  return (
    <IconButton onClick={(_) => onDismiss()}>
      <FontAwesomeIcon icon={faXmark as IconProp} />
    </IconButton>
  );
};

/**
 * This component repesents a button to abort the on-going transfer upload.
 *
 * @returns JSX element representing upload abort button.
 */
const UploadAbortButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const { isMobileWidth } = useWindowDimensions();
  return isMobileWidth ? (
    <Button type="button" variant="text" onClick={onClick} disabled={disabled}>
      {t(`Cancel`)}
    </Button>
  ) : (
    <Tooltip
      title={t(
        `Cancels the transfer upload. Canceling will remove all uploaded data and incomplete files.`
      )}
    >
      <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={(theme) => ({
          borderRadius: "50%",
          width: 36,
          height: 36,
          padding: 0,
          overflow: "hidden",
          color: "#C40606",
          "&.Mui-disabled": {
            color: "action.disabled",
          },
          "&:hover": {
            // match hover color with focus color
            backgroundColor: alpha(
              "#C40606",
              theme.palette.action.focusOpacity
            ),
          },
        })}
      >
        <CloseIcon
          sx={{
            width: 18,
            color: "#C40606",
          }}
        />
      </IconButton>
    </Tooltip>
  );
};
